import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { MaterialReactTable, MRT_ToggleDensePaddingButton, MRT_ToggleFiltersButton, MRT_ToggleFullScreenButton, MRT_ToggleGlobalFilterButton, useMaterialReactTable } from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';
import { Box, Chip, IconButton, Menu, MenuItem, Skeleton, Typography, Tooltip, LinearProgress, Stack, Avatar } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BugReportIcon from '@mui/icons-material/BugReport';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { mkConfig, generateCsv, download } from 'export-to-csv';

const csvConfig = mkConfig({ fieldSeparator: ',', decimalSeparator: '.', useKeysAsHeaders: true });

const STATUS_COLOR = { 'Nouveau': 'info', 'En cours': 'warning', 'Résolu': 'success', 'Fermé': 'default', 'Rejeté': 'error' };
const getStatusColor = (s) => STATUS_COLOR[s] || 'default';
const PRIORITY_COLOR = { 'Basse': 'success', 'Normale': 'info', 'Haute': 'warning', 'Urgente': 'error', 'Immédiate': 'error' };
const getPriorityColor = (p) => PRIORITY_COLOR[p] || 'default';

const TrackerIcon = ({ tracker }) => {
    if (!tracker) return null;
    return tracker.toLowerCase().includes('bug')
        ? <BugReportIcon fontSize="small" color="error" />
        : <AssignmentIcon fontSize="small" color="action" />;
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : 'N/A';
const getInitials = (n = '') => n.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2) || '?';
const stringToColor = (s = '') => { let h = 0; for (let i = 0; i < s.length; i++) h = s.charCodeAt(i) + ((h << 5) - h); return `hsl(${h % 360}, 55%, 45%)`; };

// Convertit les filtres MRT en params API
function buildFilterParams(columnFilters, globalFilter) {
    const params = {};
    if (globalFilter) params.search = globalFilter;
    for (const f of columnFilters) {
        const val = f.value;
        if (val === undefined || val === null || val === '') continue;
        switch (f.id) {
            case 'subject': params.search = val; break;
            case 'status': params.status = val; break;
            case 'priority': params.priority = val; break;
            case 'tracker': params.tracker = val; break;
            case 'assignedTo': params.assignedTo = val; break;
            case 'project': params.projectName = val; break;
            case 'organization': params.orgName = val; break;
            case 'totalHours':
                if (Array.isArray(val)) {
                    if (val[0] != null && val[0] !== '') params.hoursMin = val[0];
                    if (val[1] != null && val[1] !== '') params.hoursMax = val[1];
                }
                break;
            case 'done_ratio':
                if (Array.isArray(val)) {
                    if (val[0] != null && val[0] !== '') params.ratioMin = val[0];
                    if (val[1] != null && val[1] !== '') params.ratioMax = val[1];
                }
                break;
            default: break;
        }
    }
    return params;
}

const EasyRedmineTickets = ({ orgId }) => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [total, setTotal] = useState(0);

    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });
    const [columnFilters, setColumnFilters] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [sorting, setSorting] = useState([]);

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const fetchTickets = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const filterParams = buildFilterParams(columnFilters, globalFilter);
            const sortParam = sorting[0]?.id || '';
            const sortDirParam = sorting[0]?.desc ? 'desc' : 'asc';
            const params = {
                page: pagination.pageIndex + 1,
                limit: pagination.pageSize,
                ...(orgId ? { orgId } : {}),
                ...(sortParam ? { sort: sortParam, sortDir: sortDirParam } : {}),
                ...filterParams,
            };
            const { data } = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/easyredmine/tickets`,
                { params }
            );
            setTickets(data?.data || []);
            setTotal(data?.pagination?.total || 0);
        } catch (e) {
            setError(e?.response?.data?.error || e.message || 'Erreur');
        } finally {
            setLoading(false);
        }
    }, [pagination.pageIndex, pagination.pageSize, columnFilters, globalFilter, sorting, orgId]);

    // Reset page quand les filtres changent
    useEffect(() => { setPagination(p => ({ ...p, pageIndex: 0 })); }, [columnFilters, globalFilter]);
    useEffect(() => { fetchTickets(); }, [fetchTickets]);

    const buildCsvRow = (t) => ({
        id: t.ticketId, sujet: t.subject || '', statut: t.status || '',
        priorite: t.priority || '', tracker: t.tracker || '',
        projet: t.project?.name || t.projectName || '',
        organisation: t.organization?.name || '',
        assigne_a: t.assignedTo || '', auteur: t.author || '',
        heures_total: t.totalHours ?? '', heures_estimees: t.estimated_hours ?? '',
        avancement: t.done_ratio != null ? `${t.done_ratio}%` : '',
        date_debut: fmtDate(t.start_date), echeance: fmtDate(t.due_date),
        cree_le: fmtDate(t.er_created_on), mis_a_jour: fmtDate(t.er_updated_on),
    });
    const handleExportRows = (rows) => { download(csvConfig)(generateCsv(csvConfig)(rows.map(r => buildCsvRow(r.original)))); setAnchorEl(null); };
    const handleExportAll = () => { download(csvConfig)(generateCsv(csvConfig)(tickets.map(buildCsvRow))); setAnchorEl(null); };

    const columns = useMemo(() => [{
        id: 'ticket',
        columns: [
            {
                accessorKey: 'ticketId', header: '#', size: 70, enableColumnFilter: false,
                Cell: ({ renderedCellValue }) => (
                    <Typography variant="caption" color="text.secondary" fontFamily="monospace">#{renderedCellValue}</Typography>
                ),
            },
            {
                accessorKey: 'subject', header: 'Sujet', size: 340, filterVariant: 'text',
                Cell: ({ row }) => (
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <TrackerIcon tracker={row.original.tracker} />
                        <Typography variant="body2" noWrap title={row.original.subject}>{row.original.subject}</Typography>
                    </Stack>
                ),
            },
            {
                accessorKey: 'status', header: 'Statut', size: 120,
                filterVariant: 'select',
                filterSelectOptions: ['Nouveau', 'En cours', 'Résolu', 'Fermé', 'Rejeté'],
                Cell: ({ renderedCellValue }) => (
                    <Chip label={renderedCellValue || 'N/A'} color={getStatusColor(renderedCellValue)} size="small" variant="outlined" />
                ),
            },
            {
                accessorKey: 'priority', header: 'Priorité', size: 100,
                filterVariant: 'select',
                filterSelectOptions: ['Basse', 'Normale', 'Haute', 'Urgente', 'Immédiate'],
                Cell: ({ renderedCellValue }) => (
                    <Chip label={renderedCellValue || 'N/A'} color={getPriorityColor(renderedCellValue)} size="small" />
                ),
            },
            {
                accessorKey: 'tracker', header: 'Tracker', size: 110, filterVariant: 'text',
                Cell: ({ renderedCellValue }) => <Chip label={renderedCellValue || 'N/A'} size="small" variant="outlined" />,
            },
            {
                accessorFn: (row) => row.project?.name || row.projectName || '',
                id: 'project', header: 'Projet', size: 200, filterVariant: 'text',
                Cell: ({ renderedCellValue, row }) => (
                    <Tooltip title={row.original.project?.parentProjectName || ''} placement="top">
                        <Typography variant="body2" noWrap>{renderedCellValue}</Typography>
                    </Tooltip>
                ),
            },
            {
                accessorFn: (row) => row.organization?.name || '',
                id: 'organization', header: 'Organisation', size: 160, filterVariant: 'text',
                Cell: ({ renderedCellValue }) => <Chip label={renderedCellValue || 'N/A'} size="small" variant="outlined" />,
            },
            {
                accessorKey: 'assignedTo', header: 'Assigné à', size: 150, filterVariant: 'text',
                Cell: ({ renderedCellValue }) => {
                    if (!renderedCellValue) return <Typography variant="caption" color="text.secondary">—</Typography>;
                    return (
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar sx={{ width: 24, height: 24, fontSize: 10, bgcolor: stringToColor(renderedCellValue) }}>
                                {getInitials(renderedCellValue)}
                            </Avatar>
                            <Typography variant="body2" noWrap>{renderedCellValue}</Typography>
                        </Stack>
                    );
                },
            },
            {
                accessorFn: (row) => row.totalHours ?? 0,
                id: 'totalHours', header: 'Heures', size: 120, filterVariant: 'range',
                Cell: ({ row }) => {
                    const spent = row.original.totalHours ?? 0;
                    const est = row.original.estimated_hours;
                    return (
                        <Stack spacing={0.3}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <AccessTimeIcon sx={{ fontSize: 12 }} color="action" />
                                <Typography variant="caption">{spent}h {est ? `/ ${est}h` : ''}</Typography>
                            </Stack>
                            {est > 0 && (
                                <LinearProgress variant="determinate" value={Math.min((spent / est) * 100, 100)}
                                    color={spent > est ? 'error' : 'primary'} sx={{ height: 4, borderRadius: 2, width: 80 }} />
                            )}
                        </Stack>
                    );
                },
            },
            {
                accessorFn: (row) => row.done_ratio ?? 0,
                id: 'done_ratio', header: 'Avancement', size: 120, filterVariant: 'range',
                Cell: ({ renderedCellValue }) => (
                    <Stack spacing={0.3}>
                        <Typography variant="caption">{renderedCellValue}%</Typography>
                        <LinearProgress variant="determinate" value={renderedCellValue} sx={{ height: 4, borderRadius: 2, width: 80 }} />
                    </Stack>
                ),
            },
            {
                accessorFn: (row) => row.due_date || '',
                id: 'due_date', header: 'Échéance', size: 110, enableColumnFilter: false,
                Cell: ({ row }) => {
                    const due = row.original.due_date;
                    if (!due) return <Typography variant="caption" color="text.secondary">—</Typography>;
                    const overdue = new Date(due) < new Date() && row.original.status !== 'Fermé';
                    return <Typography variant="caption" color={overdue ? 'error' : 'text.primary'}>{fmtDate(due)}</Typography>;
                },
            },
            {
                accessorFn: (row) => fmtDate(row.er_updated_on),
                id: 'er_updated_on', header: 'MAJ', size: 100, enableColumnFilter: false,
            },
        ],
    }], []);

    const table = useMaterialReactTable({
    localization: MRT_Localization_FR,
    enableGlobalFilter: true,
    enableColumnFilters: true,

        columns,
        data: tickets,
        enableGrouping: true,

        manualPagination: true,
        manualFiltering: true,
        manualSorting: true,
        rowCount: total,
        onPaginationChange: setPagination,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        state: { showGlobalFilter: true, showColumnFilters: true,  pagination, columnFilters, globalFilter, sorting, isLoading: loading },
        enableColumnFilterModes: true,
        enableColumnOrdering: true,
        enableColumnPinning: true,
        initialState: {
      showColumnFilters: true,
      showGlobalFilter: true,

            density: 'compact',
            columnPinning: { left: ['mrt-row-expand', 'mrt-row-select'], right: ['mrt-row-actions'] },
            pagination: { pageSize: 50 },
            columnVisibility: { er_updated_on: false },
        },
        paginationDisplayMode: 'pages',
        positionToolbarAlertBanner: 'bottom',
        muiSearchTextFieldProps: { size: 'small', variant: 'outlined' },
        muiPaginationProps: { color: 'secondary', rowsPerPageOptions: [25, 50, 100, 200], shape: 'rounded', variant: 'outlined' },

        renderDetailPanel: ({ row }) => {
            const ticket = row.original;
            const customFields = ticket.customFields || [];
            const timeEntries = ticket.timeEntries || [];
            return (
                <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
                        {ticket.author && <Chip label={`Auteur: ${ticket.author}`} size="small" variant="outlined" />}
                        {ticket.er_created_on && <Chip label={`Créé: ${fmtDate(ticket.er_created_on)}`} size="small" variant="outlined" />}
                        {ticket.start_date && <Chip label={`Début: ${fmtDate(ticket.start_date)}`} size="small" variant="outlined" />}
                        {ticket.timeEntryCount != null && (
                            <Chip icon={<AccessTimeIcon />} label={`${ticket.timeEntryCount} saisie(s) — ${ticket.totalHours}h`}
                                size="small" color="primary" variant="outlined" />
                        )}
                    </Stack>
                    {ticket.description && (
                        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Description</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxHeight: 120, overflow: 'auto' }}
                                dangerouslySetInnerHTML={{ __html: ticket.description }} />
                        </Box>
                    )}
                    {customFields.length > 0 && (
                        <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
                            {customFields.filter(cf => cf.value).map(cf => (
                                <Chip key={cf.id} label={`${cf.name}: ${cf.value}`} size="small" variant="outlined" />
                            ))}
                        </Stack>
                    )}
                    {timeEntries.length > 0 && (
                        <>
                            <Typography variant="subtitle2" mb={1}>Saisies de temps</Typography>
                            <DataGrid autoHeight density="compact" disableRowSelectionOnClick
                                rows={timeEntries.map((te, idx) => ({
                                    id: te.entryId || idx, date: fmtDate(te.spentOn),
                                    user: te.user || '—', activity: te.activity || '—',
                                    hours: te.hours ?? 0, comments: te.comments || '—',
                                }))}
                                columns={[
                                    { field: 'date', headerName: 'Date', width: 100 },
                                    { field: 'user', headerName: 'Utilisateur', flex: 1 },
                                    { field: 'activity', headerName: 'Activité', flex: 1 },
                                    { field: 'hours', headerName: 'Heures', width: 80 },
                                    { field: 'comments', headerName: 'Commentaire', flex: 2 },
                                ]}
                                pageSizeOptions={[5, 10]}
                                initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
                            />
                        </>
                    )}
                </Box>
            );
        },

        renderToolbarInternalActions: ({ table }) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}><FileDownloadIcon /></IconButton>
                <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
                    <MenuItem disabled={table.getRowModel().rows.length === 0}
                        onClick={() => handleExportRows(table.getRowModel().rows)}>Exporter la page actuelle</MenuItem>
                    <MenuItem disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
                        onClick={() => handleExportRows(table.getSelectedRowModel().rows)}>Exporter les lignes sélectionnées</MenuItem>
                    <MenuItem disabled={tickets.length === 0} onClick={handleExportAll}>Exporter tout (page courante)</MenuItem>
                </Menu>
                <MRT_ToggleDensePaddingButton table={table} />
                <MRT_ToggleFullScreenButton table={table} />
                <MRT_ToggleFiltersButton table={table} />
                <MRT_ToggleGlobalFilterButton table={table} />
            </Box>
        ),
    });

    return (
        <Box>
            <Typography variant="h6" my={1} mb={3}>Tickets EasyRedmine</Typography>
            {error ? (
                <Box sx={{ p: 2 }}><Typography color="error">{error}</Typography></Box>
            ) : loading && tickets.length === 0 ? (
                <Skeleton variant="rectangular" width="100%" height={600} />
            ) : tickets.length === 0 && !loading ? (
                <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h6" color="textSecondary">Aucun ticket disponible</Typography>
                </Box>
            ) : (
                <MaterialReactTable table={table} />
            )}
        </Box>
    );
};

export default EasyRedmineTickets;